import {
  type ElementType,
  FC,
  forwardRef,
  type ReactNode,
  type ReactElement,
} from 'react'

import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

import { BackgroundProvider } from '../BackgroundContext/index.js'
import { BleedProvider } from '../Bleed/BleedContext.js'
import {
  PolymorphicComponentPropsWithRef,
  PolymorphicRef,
} from '../types/polymorphic.js'
import { CardPadding, CardProvider, useCard } from './CardContext.js'
import { CardActions, CardFooter } from './CardFooter.js'
import { CardHeader } from './CardHeader.js'

interface Props {
  children: ReactNode
  transparent?: boolean
  padding?: CardPadding
  attached?: 'top' | 'bottom' | 'left' | 'right' | 'all' | 'none'
}
export type CardProps<C extends ElementType> = PolymorphicComponentPropsWithRef<
  C,
  Props
>
type CardComponent = <C extends ElementType = 'div'>(
  props: CardProps<C>,
) => ReactElement | null

/**
 * A “card” is a UI design pattern that groups related information in a flexible-size container visually resembling a playing card.
 */

export type CompoundCardComponent = CardComponent & {
  Header?: typeof CardHeader
  Content?: typeof CardContent
  Footer?: typeof CardFooter
  Media?: typeof CardMedia
  Actions?: typeof CardActions
}

export const Card: CompoundCardComponent = forwardRef(
  <C extends React.ElementType = 'div'>(
    {
      as,
      children,
      className,
      transparent = false,
      padding = 'md',
      attached = 'none',
      ...rest
    }: CardProps<C>,
    ref?: PolymorphicRef<C>,
  ) => {
    let attachedClsx = 'sm:rounded-lg'
    if (attached === 'top') {
      attachedClsx = 'sm:rounded-b-lg'
    }
    if (attached === 'bottom') {
      attachedClsx = 'sm:rounded-t-lg'
    }
    if (attached === 'left') {
      attachedClsx = 'sm:rounded-e-lg'
    }
    if (attached === 'right') {
      attachedClsx = 'sm:rounded-s-lg'
    }
    if (attached === 'all') {
      attachedClsx = ''
    }

    const Component = as || 'div'

    return (
      <Component
        ref={ref}
        className={twMerge(
          clsx(
            !transparent && 'bg-surface text-content-subdued shadow',
            'flex flex-col justify-between',
            attachedClsx,
            className,
          ),
        )}
        {...rest}
      >
        <CardProvider padding={padding}>
          <BackgroundProvider backgroundType="surface">
            {children}
          </BackgroundProvider>
        </CardProvider>
      </Component>
    )
  },
)

export type CardContentProps = React.ComponentProps<'div'> & {
  withBleed?: boolean
}

const CardContent: FC<CardContentProps> = ({
  children,
  className,
  withBleed = false,
  ...rest
}) => {
  const { padding } = useCard()

  return (
    <div
      className={twMerge(
        clsx(
          'flex-1',
          padding === 'md' && 'px-4 py-5 sm:p-6',
          padding === 'sm' && 'px-4 py-5 sm:px-3',
          className,
        ),
      )}
      {...rest}
    >
      {withBleed ? (
        <BleedProvider
          bleedClassName={clsx(
            padding === 'md' && '-ms-4 -me-4 sm:-mx-6',
            padding === 'sm' && '-ms-4 -me-4 sm:-mx-3',
          )}
        >
          {children}
        </BleedProvider>
      ) : (
        children
      )}
    </div>
  )
}

export type CardMediaProps = React.ComponentProps<'div'>

const CardMedia: FC<CardMediaProps> = ({ children, className, ...rest }) => (
  <div className={className} {...rest}>
    {children}
  </div>
)

Card.Header = CardHeader
Card.Content = CardContent
Card.Footer = CardFooter
Card.Media = CardMedia
Card.Actions = CardActions
